import { listenTrialMaxWords } from '@/environment';
import { useTiming } from '@/hooks/useTiming';
import {
    getPageProperty,
    PolotnoStoreActivePageKeys,
    setPageProperty,
    usePolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks/polotno-store';
import { useCurrentProjectId } from '@/pages/AdvancedEditor/hooks/useCurrentProjectId';
import { uploadAudio, uploadFile } from '@/services/api';
import { DidUser, useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { WordLevelTimestamp } from '@/utils/types';
import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { Page } from '../AdvancedEditor';
import { useAdvancedEditorContext } from '../context/useAdvancedEditorContext';
import { clearAudioCache } from './scenesPreview/scenes-preview';
import { isTtsOverLimit } from './useIsTtsOverLimit';
import { setPageDuration } from './useUpdateDuration';
import { getAudioResultUrl } from './useUploadAudioFile';

export enum CacheStatus {
    Empty = 'empty',
    Existing = 'existing',
    Updating = 'updating',
    Invalid = 'invalid',
}

export function useUploadActivePageTtsCache() {
    const projectId = useCurrentProjectId();
    const [ttsAudioCacheStatus] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
    const uploadControllerRef = useRef<AbortController | null>(null);
    const { store } = useAdvancedEditorContext();

    useEffect(() => {
        if (ttsAudioCacheStatus === CacheStatus.Invalid) {
            uploadControllerRef.current?.abort();
        }
    }, [ttsAudioCacheStatus, projectId]);

    const uploadTtsCache = useUploadTtsCache();

    return async (buffer: ArrayBuffer, text: string, duration: number, wordLevelTiming?: WordLevelTimestamp[]) =>
        uploadTtsCache(store.activePage, buffer, text, duration, wordLevelTiming);
}

export const getTtsResultUrl = ({ user, projectId, pageId }: { projectId: string; user?: DidUser | null; pageId: string }) => {
    const audioResultUrl = getAudioResultUrl({ user, projectId, pageId });

    return audioResultUrl.slice(0, audioResultUrl.lastIndexOf('/'));
};

export const useUploadTtsCache = () => {
    const { user, isAnonymous } = useAuth();
    const projectId = useCurrentProjectId();
    const uploadControllerRef = useRef<AbortController | null>(null);
    const { withTiming } = useTiming();

    async function upload(page: Page, buffer: ArrayBuffer, duration: number, wordLevelTiming?: WordLevelTimestamp[]) {
        const ttsAudioCacheStatus = getPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
        const setTtsAudioCacheStatus = setPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
        const setWordLevelTimestampsUrl = setPageProperty(page, PolotnoStoreActivePageKeys.wordLevelTimestampsUrl);
        const setTtsCreated = setPageProperty(page, PolotnoStoreActivePageKeys.ttsCreated);
        const setSceneDurationCalculated = setPageProperty(page, PolotnoStoreActivePageKeys.sceneDurationCalculated);

        if (!projectId || ttsAudioCacheStatus === CacheStatus.Invalid) {
            return;
        }

        const audioResultUrl = getAudioResultUrl({ user, projectId, pageId: page.id });

        const audioBlob = new Blob([buffer]);
        const audioFile = new File([audioBlob], `audio_${nanoid()}.wav`, { type: 'audio/wav' });
        const wordLevelTimingBlob = wordLevelTiming ? new Blob([JSON.stringify(wordLevelTiming)]) : undefined;
        const wordLevelTimingJsonFile = wordLevelTimingBlob
            ? new File([wordLevelTimingBlob], 'wordLevelTiming.json', { type: 'application/json' })
            : undefined;

        const nextController = new AbortController();
        uploadControllerRef.current = nextController;

        setTtsAudioCacheStatus(CacheStatus.Updating);
        clearAudioCache(page);
        setSceneDurationCalculated(true);
        setPageDuration(page, duration * 1000);
        setTtsCreated(true);

        const localAudioUrl = URL.createObjectURL(audioBlob);
        setPageProperty(page, PolotnoStoreActivePageKeys.cachedAudio)(localAudioUrl);

        await uploadAudio({
            audio: audioFile,
            resultUrl: audioResultUrl,
            signal: nextController.signal,
        });

        const wordLevelTimingJsonUrl = wordLevelTimingJsonFile
            ? await uploadFile(wordLevelTimingJsonFile, nextController.signal)
            : undefined;

        setWordLevelTimestampsUrl(wordLevelTimingJsonUrl);

        setTtsAudioCacheStatus(CacheStatus.Existing);
    }

    return async (page: Page, buffer: ArrayBuffer, text: string, duration: number, wordLevelTiming?: WordLevelTimestamp[]) => {
        const wordLimitExceeded = isTtsOverLimit(text, listenTrialMaxWords, user, isAnonymous);
        const ttsAudioCacheStatus = getPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus);
        const setTtsAudioCacheStatus = setPageProperty(page, PolotnoStoreActivePageKeys.ttsAudioCacheStatus);

        await withTiming('useUploadTtsCache', async () => {
            try {
                if (isAnonymous) {
                    return;
                }
                if (!wordLimitExceeded) {
                    await upload(page, buffer, duration, wordLevelTiming);
                } else {
                    if (ttsAudioCacheStatus !== CacheStatus.Empty) {
                        setTtsAudioCacheStatus(CacheStatus.Invalid);
                    }
                }
            } catch (e: any) {
                setTtsAudioCacheStatus(CacheStatus.Invalid);

                if (e.code !== 'ERR_CANCELED') {
                    logger.error('Failed to upload audio (cached tts)', { e });
                }
                throw e;
            }
        });
    };
};
